import type { IdeaStatus, ImageType } from '@vizit/api';
import * as React from 'react';

export const API_IMAGE_TYPE = {
  Hero: 'hero',
  Carousel: 'carousel',
} as const satisfies Record<string, ImageType>;

export enum SparkState {
  Loading = 'loading',
  Error = 'error',
  UnavailableHero = 'unavailable_hero',
  UnavailableNoScore = 'unavailable_no_score',
  /** The API can't run Spark on this image yet, though it has a score */
  UnavailableNotReady = 'unavailable_not_ready',
  Success = 'success',
}

export enum IdeaStatus_ {
  Idle = 'idle',
  Loading = 'loading',
  Polling = 'polling',
  Completed = 'completed',
  Error = 'error',
  UnavailableHero = 'unavailable-hero',
  UnavailableNoScore = 'unavailable-no-score',
  UnavailableNotReady = 'unavailable-not-ready',
}

export interface IdeaState {
  status: IdeaStatus_;
  content: string | null;
  ideaId: string | null;
  assetId: string | null;
  hasGenerated: boolean;
  pollingStartedAt: number | null;
}

export enum IdeaActionType {
  Reset = 'RESET',
  SetUnavailableHero = 'SET_UNAVAILABLE_HERO',
  SetUnavailableNoScore = 'SET_UNAVAILABLE_NO_SCORE',
  SetUnavailableNotReady = 'SET_UNAVAILABLE_NOT_READY',
  StartLoading = 'START_LOADING',
  StartPolling = 'START_POLLING',
  Complete = 'COMPLETE',
  Error = 'ERROR',
  Timeout = 'TIMEOUT',
}

export type IdeaAction =
  | { type: IdeaActionType.Reset; assetId: string }
  | { type: IdeaActionType.SetUnavailableHero }
  | { type: IdeaActionType.SetUnavailableNoScore }
  | { type: IdeaActionType.SetUnavailableNotReady }
  | { type: IdeaActionType.StartLoading }
  | { type: IdeaActionType.StartPolling; ideaId: string }
  | { type: IdeaActionType.Complete; content: string }
  | { type: IdeaActionType.Error }
  | { type: IdeaActionType.Timeout };

export const POLLING_TIMEOUT_MS = 2.5 * 60 * 1000;
export const POLLING_INTERVAL_MS = 3000;
export const SPARK_TAB_ID = 'spark-ideas';

export const API_IDEA_STATUS = {
  Requested: 'requested',
  Processing: 'processing',
  Completed: 'completed',
  Failed: 'failed',
} as const satisfies Record<string, IdeaStatus>;

export const createInitialState = (assetId: string | null = null): IdeaState => ({
  status: IdeaStatus_.Idle,
  content: null,
  ideaId: null,
  assetId,
  hasGenerated: false,
  pollingStartedAt: null,
});

export function ideaReducer(state: IdeaState, action: IdeaAction): IdeaState {
  switch (action.type) {
    case IdeaActionType.Reset:
      return createInitialState(action.assetId);

    case IdeaActionType.SetUnavailableHero:
      return { ...state, status: IdeaStatus_.UnavailableHero, content: null, ideaId: null };

    case IdeaActionType.SetUnavailableNoScore:
      return { ...state, status: IdeaStatus_.UnavailableNoScore, content: null, ideaId: null };

    case IdeaActionType.SetUnavailableNotReady:
      return { ...state, status: IdeaStatus_.UnavailableNotReady, content: null, ideaId: null };

    case IdeaActionType.StartLoading:
      return { ...state, status: IdeaStatus_.Loading, content: null, ideaId: null };

    case IdeaActionType.StartPolling:
      return {
        ...state,
        status: IdeaStatus_.Polling,
        ideaId: action.ideaId,
        pollingStartedAt: Date.now(),
      };

    case IdeaActionType.Complete:
      return {
        ...state,
        status: IdeaStatus_.Completed,
        content: action.content,
        ideaId: null,
        hasGenerated: true,
        pollingStartedAt: null,
      };

    case IdeaActionType.Error:
    case IdeaActionType.Timeout:
      return {
        ...state,
        status: IdeaStatus_.Error,
        content: null,
        ideaId: null,
        pollingStartedAt: null,
      };

    default:
      return state;
  }
}

export interface Idea {
  id?: string;
  status: IdeaStatus;
  output?: string | null;
}

export const findLatestInProgressIdea = (ideas: Idea[]): Idea | null => {
  const inProgressIdeas = ideas.filter(
    (idea) => idea.status === API_IDEA_STATUS.Requested || idea.status === API_IDEA_STATUS.Processing,
  );
  return inProgressIdeas.length > 0 ? inProgressIdeas[0] : null;
};

export const findLatestCompletedIdea = (ideas: Idea[]): Idea | null => {
  const completedIdeas = ideas.filter((idea) => idea.status === API_IDEA_STATUS.Completed);
  return completedIdeas.length > 0 ? completedIdeas[0] : null;
};

export const shouldAutoGenerate = (currentTab: string, ideas: Idea[]): boolean => {
  return currentTab === SPARK_TAB_ID && ideas.length === 0;
};

export const convertToSparkState = (status: IdeaStatus_): SparkState => {
  switch (status) {
    case IdeaStatus_.Idle:
    case IdeaStatus_.Loading:
    case IdeaStatus_.Polling:
      return SparkState.Loading;
    case IdeaStatus_.Completed:
      return SparkState.Success;
    case IdeaStatus_.Error:
      return SparkState.Error;
    case IdeaStatus_.UnavailableHero:
      return SparkState.UnavailableHero;
    case IdeaStatus_.UnavailableNoScore:
      return SparkState.UnavailableNoScore;
    case IdeaStatus_.UnavailableNotReady:
      return SparkState.UnavailableNotReady;
    default:
      return SparkState.Loading;
  }
};

/**
 * Only a Loading state acts on search results. Every other status, an error above all, waits for the user (or a
 * recovered search) to start over, so a persistent API error can't turn the auto-generate effect into a request loop.
 */
export const canProcessSearchResults = (state: IdeaState, isGenerating: boolean): boolean =>
  state.status === IdeaStatus_.Loading && !isGenerating && !state.hasGenerated;

interface UseSparksTabStateProps {
  assetId: string;
}

function useSparksTabState(props: UseSparksTabStateProps) {
  const { assetId } = props;
  const [state, dispatch] = React.useReducer(ideaReducer, assetId, (id) => createInitialState(id));

  const sparkState = React.useMemo(() => convertToSparkState(state.status), [state.status]);

  const reset = React.useCallback((newAssetId: string) => {
    dispatch({ type: IdeaActionType.Reset, assetId: newAssetId });
  }, []);

  const setUnavailableHero = React.useCallback(() => {
    dispatch({ type: IdeaActionType.SetUnavailableHero });
  }, []);

  const setUnavailableNoScore = React.useCallback(() => {
    dispatch({ type: IdeaActionType.SetUnavailableNoScore });
  }, []);

  const setUnavailableNotReady = React.useCallback(() => {
    dispatch({ type: IdeaActionType.SetUnavailableNotReady });
  }, []);

  const startLoading = React.useCallback(() => {
    dispatch({ type: IdeaActionType.StartLoading });
  }, []);

  const startPolling = React.useCallback((ideaId: string) => {
    dispatch({ type: IdeaActionType.StartPolling, ideaId });
  }, []);

  const complete = React.useCallback((content: string) => {
    dispatch({ type: IdeaActionType.Complete, content });
  }, []);

  const setError = React.useCallback(() => {
    dispatch({ type: IdeaActionType.Error });
  }, []);

  const timeout = React.useCallback(() => {
    dispatch({ type: IdeaActionType.Timeout });
  }, []);

  return {
    state,
    sparkState,
    reset,
    setUnavailableHero,
    setUnavailableNoScore,
    setUnavailableNotReady,
    startLoading,
    startPolling,
    complete,
    setError,
    timeout,
  };
}

export { useSparksTabState };
